import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MainMapService } from '../../../../shared/service/main-map.service';
import { ArbeitsbereichService } from '../../../../shared/service/arbeitsbereich.service';
import { IdentifyService } from '../../../../shared/service/identify.service';
import { DetailViewNotifyService } from '../../../detail-view/shared/detailview-notify.service';
import { DetailViewFeatureParams } from '../../../detail-view/detail-view.model';
import { WidgetNotifyService } from '../../../../shared/service/widget-notify.service';
import { AttributesService } from '../../../../shared/service/attributes.service';
import { ConfigService } from '../../../../config/config.service';
import { ErrorHandlingService } from '../../../../core/service/error-handling.service';
import { LayerContainerService } from '../../../../shared/service/layer-container.service';
import { SketchService } from '../service/sketch.service';
import {
  EsriReactiveUtils,
  EsriSimpleFillSymbol,
  EsriSimpleLineSymbol,
  EsriSimpleMarkerSymbol,
  EsriSketch,
} from '../../../../config/esri.module';
import { CONSTANTS_VALUE } from '../../../../config/constants.module';
import {isGeometryType, TOOL_MAP} from "../../../../shared/model/sketch-geometry.model";
import {TakeUntilDestroy} from "../../../../core/component/take-until-destroy";
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'sketch',
    templateUrl: './sketch.component.html',
    styleUrls: ['sketch.component.scss'],
    standalone: false
})
export class SketchComponent extends TakeUntilDestroy implements OnInit, OnDestroy {
  @ViewChild('sketchRef', { static: true }) sketchRef: ElementRef;

  private _mapView: __esri.MapView;
  private _editableLayers: Array<__esri.FeatureLayer> = [];
  private _selectedLayer: __esri.FeatureLayer;
  private _activated = false;
  private _sketch: __esri.Sketch;

  constructor(
    private _mainMapService: MainMapService,
    private _identifyService: IdentifyService,
    private _attributesService: AttributesService,
    private _detailViewNotifyService: DetailViewNotifyService,
    private _configService: ConfigService,
    private _errorService: ErrorHandlingService,
    private _widgetNotifyService: WidgetNotifyService,
    private _layerContainerService: LayerContainerService,
    private _arbeitsbereichService: ArbeitsbereichService,
    private _sketchService: SketchService,
    private _cdr: ChangeDetectorRef
  ) {
    super();
  }

  /**
   * @name ngOnInit
   * @description Creates expand-widget and a draw-instance and subscribes the listeners
   */
  ngOnInit() {
    this._mapView = this._mainMapService.mapView;

    this._arbeitsbereichService.onChangeArbeitsbereichSubject.pipe(takeUntil(this.unsubscribe$)).subscribe(
      () => {
        this.stopDrawing(true);
        this.updateEditableLayers();
        if (this._sketch) this._sketch.availableCreateTools = this.getAvailableTools();
      }
    );

    this._detailViewNotifyService.featureCreatedSubject.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.deactivate();
    });
   this._detailViewNotifyService.featureCreationCanceledSubject.pipe(takeUntil(this.unsubscribe$)).subscribe(
      () => {
        this.stopDrawing(true);
      }
    );

    this.updateEditableLayers();

    this.initializeSketching();
  }

  /**
   * @name ngOnDestroy
   * @description Deactivate the tool and Unsubscribes all the listeners
   */
  ngOnDestroy() {
    this.deactivate(true);
    super.ngOnDestroy();
  }

  public get sketch(): __esri.Sketch {
    return this._sketch;
  }

  /**
   * @name initializeSketching
   * @description Creates an instance of the ArcGIS Sketch Widget.
   */
  private initializeSketching() {
    // Create Sketch widget using an app wide common sketchlayer
    // Activate only geometry types allowed by the target layer
    this._sketch = new EsriSketch({
      container: this.sketchRef.nativeElement,
      view: this._mapView,
      layer: this._sketchService.sketchLayer,
      availableCreateTools: this.getAvailableTools(),
    });
    // Disable multiselect editing
    this._sketch.defaultUpdateOptions.multipleSelectionEnabled = false;

    // Add listener on sketchlayer to disable further sketching if user already drew somethig
    EsriReactiveUtils.watch(
      () => this._sketchService.sketchLayer?.graphics.length,
      (t) => {
        if (t) {
          this._sketch.availableCreateTools = this.getAvailableTools();
          this._activated = true;
        } else {
          this._activated = false;
          this._sketch.availableCreateTools = this.getAvailableTools();
        }
      }
    );

    // Disable layer selection and identifys if user starts drawing
    EsriReactiveUtils.watch(
      () => this._sketch?.activeTool,
      (t) => {
        if (t) {
          this._identifyService.deactivate();
          this._activated = true;
        } else {
          this._identifyService.activate();
          this._activated = false;
        }
      }
    )

    // React to complete drawing
    this._sketch.on('create', evt => {
      if (evt.state === 'complete') {
        this.onDrawComplete(evt.graphic);
      } else if (evt.state === 'start') {
        this._mainMapService.deactivateMapInteraction();
      }
    });

    this._sketch.on('delete', t => {
      this._detailViewNotifyService.closeSubject.next();
    });

    // React to finish update
    this._sketch.on('update', evt => {
      const graphic = evt.graphics[0];
      if (evt.state === 'complete') {
        this.onDrawComplete(graphic, true);
        // This is a kind of a hacky workaround to not react on a delete event
      } else if (evt.state === 'start') {
        this._mainMapService.deactivateMapInteraction();
      }
    });
  }

  /**
   * @name getAvailableTools
   * @description Get allowed geometry types of target layer
   * @returns string[] of available Tools
   */
  public getAvailableTools() {
    const geometryType = this.selectedLayer?.geometryType;
    if (!geometryType) {
      return [];
    }
    if (!isGeometryType(geometryType)) return [];
    return [...TOOL_MAP[geometryType]]; // Kopie, da TOOL_MAP readonly ist
  }

  /**
   * @name onLayerChange
   * @description Change Handle for Layer select. Changes the available geometries based on the selected target layer
   */
  public onLayerChange(evt: any) {
    this._sketch.availableCreateTools = this.getAvailableTools();
  }

  /**
   * @name extendNewGraphic
   * @param graphic __esri.Graphic
   * @description Populates empty properties of a new Graphic with ESTA/App specific properties and symbology
   */
  private extendNewGraphic(graphic: __esri.Graphic) {
    const symbol: __esri.SymbolUnion = this.createSymbol(this._selectedLayer.geometryType);
    graphic.attributes = {
      OBJECTID: null,
    };
    graphic.symbol = symbol;
  }

  /**
   * @name stopDrawing
   * @description Stops the drawing on the map
   */
  private stopDrawing(cancel = false) {
    this._detailViewNotifyService.closeSubject.next();
    this._sketchService.removeAll();
    if (cancel) this._sketch.cancel();
    this._mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default);
    this._identifyService.activate();
  }

  /**
   * @name onDrawComplete
   * @description Creates the graphic and sends it to the detail-view for the attribute-editing
   * @param graphic
   * @param update
   */
  public onDrawComplete(graphic: __esri.Graphic, update = false) {
    this._mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.wait);

    if (!update) this.extendNewGraphic(graphic);

    // set default attributes
    this._attributesService.getDefaultAttributes(this._selectedLayer.id, graphic).pipe(takeUntil(this.unsubscribe$)).subscribe(
      attributes => {
        if (!update) graphic.attributes = attributes;

        // set all other attributes to null
        this._selectedLayer.fields?.forEach((field: __esri.Field) => {
          if (!graphic.attributes[field.name]) {
            graphic.attributes[field.name] = null;
          }
        });

        const featureParams: DetailViewFeatureParams = {
          currentArbeitsBereichLyrs: [{ esriLayer: this._selectedLayer }],
          displayFieldname: '',
          feature: graphic,
          layerId: this.getFkIdentifyLayerId(),
          layerName: this._selectedLayer.id,
          createMode: true,
          workspace: this._arbeitsbereichService.selectedArbeitsbereich.id,
        };

        this._widgetNotifyService.onShowFeatureSubject.next(featureParams);
        this._mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default);
      },
      error => {
        this._errorService.handleLayerError(error);
        this.stopDrawing();
      }
    );
  }

  /**
   * @name createSymbol
   * @description Creates a Symbol based on the geometry-type
   */
  private createSymbol(geometryType: string): __esri.SymbolUnion {
    switch (geometryType) {
      case 'point':
        return new EsriSimpleMarkerSymbol(this._configService.mainMapConfig.draw.symbols.point);
      case 'polyline':
        return new EsriSimpleLineSymbol(this._configService.mainMapConfig.draw.symbols.polyline);
      case 'polygon':
        return new EsriSimpleFillSymbol(this._configService.mainMapConfig.draw.symbols.polygon);
      default:
        throw new Error('not supported');
    }
  }

  /**
   * @name updateEditableLayers
   * @description Updates the editable layer list
   */
  private updateEditableLayers() {
    // get editable layers from arbeitsbereich
    this._editableLayers = [];
    if (this._arbeitsbereichService.selectedArbeitsbereich) {
      this._arbeitsbereichService.selectedArbeitsbereich.editableLayers.forEach(editableLayer => {
        const fkLayer = editableLayer.fkLayer;
        const layers = this._layerContainerService.layerList.filter(
          f => f.esriLayer.id === fkLayer && f.esriLayer.type === 'feature'
        );
        if (layers.length === 1) {
          this._editableLayers.push(layers[0].esriLayer as __esri.FeatureLayer);
        } else {
          throw new Error('Exact one feature-layer expected.');
        }
      });
    }

    // set first layer as selected
    if (this._editableLayers.length > 0) {
      this._selectedLayer = this._editableLayers[0];
    }
  }

  private getFkIdentifyLayerId(): number {
    const detailViewForms = this._configService.getDetailViewConfig(
      this._arbeitsbereichService.selectedArbeitsbereich.id
    );
    return detailViewForms.forms.find(f => f.editableLayerId === this._selectedLayer.id).fkIdentifyLayerId;
  }

  /**
   * @name deactivate
   * @description Deactivates the "create"-tool and stops the drawing
   */
  public deactivate(cancel = false) {
    this._activated = false;
    this.stopDrawing(cancel);
    this._mainMapService.activateMapInteraction();
  }

  /**
   * @name isDisabled
   * @description Shows if "create"-tool is desabled
   */
  public get isDisabled(): boolean {
    return this._editableLayers.length === 0;
  }

  /**
   * @name activated
   * @description Shows if "create"-tool is activated
   */
  public get activated(): boolean {
    return this._activated;
  }

  /**
   * @name selectedLayer
   * @description Gets selected layer
   */
  public get selectedLayer(): __esri.FeatureLayer {
    return this._selectedLayer;
  }

  public set selectedLayer(layer: __esri.FeatureLayer) {
    this._selectedLayer = layer;
  }

  /**
   * @name editableLayers
   * @description Gets editable layer list
   */
  public get editableLayers(): Array<__esri.FeatureLayer> {
    return this._editableLayers;
  }
}
